import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from 'rsuite/lib/DatePicker';
import Modal from 'rsuite/lib/Modal';

import { ButtonPrimary } from '../../../../../components/ButtonPrimary';
import { InputBox } from '../../../../../components/InfoInput';
import { DATE_PICKER_LOCALE, DATE_PICKER_SHORTCUT_OPTIONS } from './constants';
import styles from './EffectiveDateModal.module.scss';
import { EffectiveDateFormValues, effectiveDateFormSchema } from './schema';
import { EffectiveDateModalProps, EffectiveDateOption } from './types';
import { EFFECTIVE_DATE_OPTION_ORDER, EFFECTIVE_DATE_OPTIONS, formatDayMonth, getSaoPauloToday, isBeforeToday, resolvePresetDate, toApiDate } from './utils';

const getContextNoun = (selectedCount: number) => (selectedCount === 1 ? 'produto selecionado' : 'produtos selecionados');

const resolveDate = (values: EffectiveDateFormValues, today: Date) =>
  values.option === 'CUSTOM' ? values.customDate : resolvePresetDate(values.option, today);

type EffectiveDateModalContentProps = Omit<EffectiveDateModalProps, 'show'>;

// Montado só enquanto o modal está aberto: cada abertura recalcula "hoje" e volta para a opção Hoje.
const EffectiveDateModalContent = ({ selectedCount, onClose, onConfirm }: EffectiveDateModalContentProps) => {
  const dateFieldLabelId = useId();
  const today = useMemo(() => getSaoPauloToday(), []);
  const shortcuts = useMemo(
    () => DATE_PICKER_SHORTCUT_OPTIONS.map(option => ({ label: EFFECTIVE_DATE_OPTIONS[option].label, value: resolvePresetDate(option, today) })),
    [today],
  );

  const { control, handleSubmit, setValue, watch } = useForm<EffectiveDateFormValues>({
    resolver: zodResolver(effectiveDateFormSchema),
    defaultValues: { option: 'TODAY', customDate: today },
  });

  const values = watch();
  const displayedDate = resolveDate(values, today);

  const handleOptionChange = (option: EffectiveDateOption, onChange: (option: EffectiveDateOption) => void) => {
    if (option === 'CUSTOM') setValue('customDate', displayedDate);
    onChange(option);
  };

  // "Hoje" é recalculado no clique: se o modal atravessou a meia-noite de SP, vale o novo dia.
  const handleConfirm = handleSubmit(formValues => onConfirm(toApiDate(resolveDate(formValues, getSaoPauloToday()))));

  return (
    <Modal show onHide={onClose} className={styles.modal}>
      <form onSubmit={handleConfirm}>
        <Modal.Header>
          <Modal.Title>Aplicar preço com data de vigência</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={styles.context}>
            Aplicando preço para{' '}
            <b>
              {selectedCount} {getContextNoun(selectedCount)}.
            </b>{' '}
            Quando esse preço deve começar a valer?
          </p>
          <div className={styles.selection}>
            <Controller
              control={control}
              name="option"
              render={({ field: { value, onChange } }) => (
                <InputBox className={styles.options}>
                  <InputBox.RadioGroup value={value} onChange={option => handleOptionChange(option as EffectiveDateOption, onChange)}>
                    {EFFECTIVE_DATE_OPTION_ORDER.map(option => (
                      <InputBox.Radio key={option} value={option} className={styles.option}>
                        <span>{EFFECTIVE_DATE_OPTIONS[option].label}</span>
                        <InputBox.FormDescription className={styles.optionDescription}>
                          {EFFECTIVE_DATE_OPTIONS[option].description}
                          {option !== 'CUSTOM' && (
                            <>
                              {' '}
                              <strong>{formatDayMonth(resolvePresetDate(option, today))}</strong>
                            </>
                          )}
                        </InputBox.FormDescription>
                      </InputBox.Radio>
                    ))}
                  </InputBox.RadioGroup>
                </InputBox>
              )}
            />
            <div className={styles.dateField}>
              <span id={dateFieldLabelId} className={styles.dateLabel}>
                Início de vigência
              </span>
              <Controller
                control={control}
                name="customDate"
                render={({ field: { onChange } }) => (
                  <DatePicker
                    oneTap
                    cleanable={false}
                    format="DD/MM/YYYY"
                    aria-labelledby={dateFieldLabelId}
                    locale={DATE_PICKER_LOCALE}
                    ranges={shortcuts}
                    value={displayedDate}
                    disabled={values.option !== 'CUSTOM'}
                    disabledDate={date => !!date && isBeforeToday(date, today)}
                    onChange={date => date && onChange(date)}
                  />
                )}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonPrimary skin="blue" theme="ghost" type="button" onClick={onClose}>
            Fechar
          </ButtonPrimary>
          <ButtonPrimary skin="blue" theme="filled" type="submit">
            Aplicar preço
          </ButtonPrimary>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export const EffectiveDateModal = ({ show, ...props }: EffectiveDateModalProps) => (show ? <EffectiveDateModalContent {...props} /> : null);
